import { memo, useEffect, useId, useState } from 'react';
import { endOfWeek, format, isValid, parseISO, startOfWeek } from 'date-fns';
import { cn } from '@/utils/cn';

interface DateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onDateRangeChange: (range: { startDate: Date; endDate: Date }) => void;
  onReset?: () => void;
  onlyInvokeResetHandler?: boolean;
  disabled?: boolean;
  className?: string;
}

const ISO_FORMAT = 'yyyy-MM-dd';
const DISPLAY_FORMAT = 'M/d/yyyy';

function toIsoValue(date: Date): string {
  return isValid(date) ? format(date, ISO_FORMAT) : '';
}

function toDisplayValue(isoValue: string): string {
  if (!isoValue) return '';
  const parsed = parseISO(isoValue);
  return isValid(parsed) ? format(parsed, DISPLAY_FORMAT) : '';
}

function fromIsoValue(value: string): Date | null {
  if (!value) return null;
  const parsedDate = parseISO(value);
  return isValid(parsedDate) ? parsedDate : null;
}

function getCurrentWeekRange(): { startDate: Date; endDate: Date } {
  const today = new Date();
  return {
    startDate: startOfWeek(today, { weekStartsOn: 1 }),
    endDate: endOfWeek(today, { weekStartsOn: 1 }),
  };
}

export const DateRangePicker = memo(function DateRangePicker({
  startDate,
  endDate,
  onDateRangeChange,
  onReset,
  onlyInvokeResetHandler = false,
  disabled = false,
  className,
}: Readonly<DateRangePickerProps>) {
  const startDateId = useId();
  const endDateId = useId();
  const [draftStartDate, setDraftStartDate] = useState(() => toIsoValue(startDate));
  const [draftEndDate, setDraftEndDate] = useState(() => toIsoValue(endDate));

  useEffect(() => {
    setDraftStartDate(toIsoValue(startDate));
  }, [startDate]);

  useEffect(() => {
    setDraftEndDate(toIsoValue(endDate));
  }, [endDate]);

  const parsedStartDate = fromIsoValue(draftStartDate);
  const parsedEndDate = fromIsoValue(draftEndDate);
  const hasInvalidRange =
    !parsedStartDate || !parsedEndDate || parsedEndDate < parsedStartDate;

  function handleApply() {
    if (hasInvalidRange) return;
    onDateRangeChange({ startDate: parsedStartDate, endDate: parsedEndDate });
  }

  function handleReset() {
    onReset?.();
    if (onlyInvokeResetHandler) return;

    const range = getCurrentWeekRange();
    setDraftStartDate(toIsoValue(range.startDate));
    setDraftEndDate(toIsoValue(range.endDate));
    onDateRangeChange(range);
  }

  return (
    <div className={cn('w-full max-w-xl', className)}>
      <fieldset disabled={disabled} className="space-y-1 disabled:opacity-60">
        <legend className="text-xs font-normal text-cool-gray-11">Date Range</legend>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="grid flex-1 gap-1 sm:grid-cols-2">
            <div>
              <label htmlFor={startDateId} className="sr-only">
                Start date
              </label>
              <div className="relative">
                <input
                  id={startDateId}
                  type="date"
                  value={draftStartDate}
                  onChange={(event) => setDraftStartDate(event.target.value)}
                  className="block h-8 w-full border border-cool-gray-2 px-2 text-sm text-transparent shadow-sm focus:border-black focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-gray-50"
                  aria-label="Start date"
                />
                <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-sm text-black">
                  {toDisplayValue(draftStartDate)}
                </span>
              </div>
            </div>
            <div>
              <label htmlFor={endDateId} className="sr-only">
                End date
              </label>
              <div className="relative">
                <input
                  id={endDateId}
                  type="date"
                  value={draftEndDate}
                  onChange={(event) => setDraftEndDate(event.target.value)}
                  className="block h-8 w-full border border-cool-gray-2 px-2 text-sm text-transparent shadow-sm focus:border-black focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-gray-50"
                  aria-label="End date"
                />
                <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-sm text-black">
                  {toDisplayValue(draftEndDate)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleApply}
              disabled={hasInvalidRange}
              className="inline-flex h-8 items-center border border-dds-green bg-dds-green px-3 text-sm font-semibold text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1 disabled:cursor-not-allowed disabled:border-cool-gray-2 disabled:bg-cool-gray-2 disabled:text-cool-gray-10"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex h-8 items-center border border-cool-gray-2 bg-white px-3 text-sm font-semibold text-black hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-cool-gray-10"
            >
              Reset
            </button>
          </div>
        </div>
      </fieldset>
    </div>
  );
});